import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** ラベル */
  label?: string;
  /** エラーメッセージ */
  error?: string;
  /** ヘルプテキスト */
  helperText?: string;
  /** 入力サイズ */
  size?: 'sm' | 'md' | 'lg';
  /** 左側アイコン */
  leftIcon?: ReactNode;
  /** 全幅表示 */
  fullWidth?: boolean;
}

/** @graph-connects none */
const sizeStyles = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-sm',
  lg: 'px-4 py-3 text-base',
};

/**
 * @graph-stack cortex-admin
 * @graph-domain Engineering
 * @graph-business テキスト入力。ラベル・エラー・ヘルプテキスト・左側アイコン・フォーカスリングを統合したスタイル付きインプット
 * @graph-connects none
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helperText,
      size = 'md',
      leftIcon,
      fullWidth = false,
      className = '',
      id,
      disabled,
      ...props
    },
    ref,
  ) => {
    const inputId = id ?? `input-${Math.random().toString(36).substring(2, 11)}`;
    const hasError = Boolean(error);

    return (
      <div className={fullWidth ? 'w-full' : ''}>
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1.5"
          >
            {label}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]">
              {leftIcon}
            </div>
          )}
          <input
            ref={ref}
            id={inputId}
            disabled={disabled}
            className={`
              w-full
              bg-[var(--color-bg-surface)]
              border rounded-lg
              text-[var(--color-text-primary)]
              placeholder:text-[var(--color-text-muted)]
              transition-all duration-[var(--transition-fast)]
              focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[var(--color-bg-base)]
              disabled:opacity-50 disabled:cursor-not-allowed
              ${
                hasError
                  ? 'border-[var(--color-error)] focus:ring-[var(--color-error)]'
                  : 'border-[var(--color-border)] hover:border-[var(--color-border-hover)] focus:border-[var(--color-border-focus)] focus:ring-[var(--color-border-focus)]'
              }
              ${sizeStyles[size]}
              ${leftIcon ? 'pl-10' : ''}
              ${className}
            `}
            aria-invalid={hasError}
            aria-describedby={
              hasError ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined
            }
            {...props}
          />
        </div>
        {error && (
          <p id={`${inputId}-error`} className="mt-1.5 text-sm text-[var(--color-error)]">
            {error}
          </p>
        )}
        {helperText && !error && (
          <p id={`${inputId}-helper`} className="mt-1.5 text-sm text-[var(--color-text-muted)]">
            {helperText}
          </p>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';
